import { FC, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { pincodeApi } from 'src/api/pincode';
import { Pincodes } from 'src/interfaces/masters/ModelMaster/model';
import { ResponseCodes } from 'src/enums/responseCodes';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { useSelection } from 'src/hooks/use-selection';
import { Status } from 'src/enums/common';

interface AddServiceAreaProps {
  open: boolean;
  onClose: () => void;
  serviceAreaIds: string[];
  addServiceAreas: (data: string[]) => Promise<boolean>;
}

export const AddServiceArea: FC<AddServiceAreaProps> = (props) => {
  const { open, onClose, serviceAreaIds, addServiceAreas } = props;
  const [isAvailable, setIsAvailable] = useState<string>('Loading');
  const [states, setStates] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string>('');
  const [districts, setDistricts] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [codes, setCodes] = useState([]);
  const [selectedcodes, setSelectedcodes] = useState([]);
  const [pincodesArray, setPincodesArray] = useState<Pincodes[]>([]);
  const [searchClicked, setSearchClicked] = useState<boolean>(false);

  // For setting height of filtered data
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
  };

  // this method maintain the selection when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      setSearchClicked(false);
      setStates([]);
      setSelectedStates('');
      setDistricts([]);
      setSelectedDistricts([]);
      setCities([]);
      setSelectedCities([]);
      setCodes([]);
      setSelectedcodes([]);
      setPincodesArray([]);
      onClose();
    }
  };

  const searchAreas = async () => {
    const filters = [];
    filters.push({
      property: 'status',
      operator: 'in',
      value: [Status.PUBLISH],
    });
    if (selectedDistricts.length) {
      filters.push({
        property: 'district',
        operator: 'in',
        value: selectedDistricts,
      });
    }
    if (selectedCities.length) {
      filters.push({
        property: 'city',
        operator: 'in',
        value: selectedCities,
      });
    }
    if (selectedcodes.length) {
      filters.push({
        property: 'code',
        operator: 'in',
        value: selectedcodes,
      });
    }
    const paginationObject: PaginationAndFilter = {
      filters,
      pageSize: 9999,
      pageNumber: 1,
      sort: -1,
      sortBy: ''
    };
    const result = await pincodeApi.getPincodesByPaginationAndFilters(
      paginationObject
    );
    const tempStore = result?.responseData?.records.map((item) => ({ ...item, id: `${item.code}#${item.area}` }));
    setPincodesArray(tempStore.filter((serviceArea) => !serviceAreaIds.includes(serviceArea.id)));
    setSearchClicked(true);
  };
  const [
    selectedPincodes,
    handleSelect,
    handleClearSelected,
    handleSelectAll,
    updateSelectedRows
  ] = useSelection(pincodesArray);

  // invoked when combination state is changed
  useEffect(() => {
    if (pincodesArray?.length === 0) {
      handleClearSelected();
    }
    // remove any pincodesArray if they were removed before
    // being unchecked
    const allCombinationIds = pincodesArray?.map((item) => item.id);

    updateSelectedRows(selectedPincodes?.filter(
      (element) => allCombinationIds?.includes(element)
    ));
  }, [pincodesArray]);

  const handleAddServiceAreas = () => {
    const addResult = addServiceAreas(selectedPincodes);
    if (addResult) {
      closeDialog('', '');
    }
  };

  const filterCodes = (condition: string, itemsArray: string[] | Number[]) => {
    pincodeApi.getDistinctValues({ property: 'code', [condition]: itemsArray.toString() })
      .then((data) => {
        setCodes(data?.responseData?.values);
      });
  };

  const getStatesDetails = async () => {
    const stateResult = await pincodeApi.getDistinctValues({ property: 'state' });
    if (stateResult.statusCode === ResponseCodes.OK) {
      setStates(stateResult.responseData?.values);
    }
    setIsAvailable(stateResult.responseData?.values?.length > 0 ? 'Success' : 'Fail');
  };
  // handle selected state
  const handleStateChange = async (event): Promise<void> => {
    setSelectedDistricts([]);
    setSelectedCities([]);
    setSelectedcodes([]);
    setSelectedStates(event.target.value);
    pincodeApi.getDistinctValues({ property: 'district', states: event.target.value.toString() })
      .then((data) => {
        setDistricts(data?.responseData?.values);
      });
    filterCodes('states', event.target.value);
  };

  // handle selected district
  const handleDistChange = async (event): Promise<void> => {
    setSelectedCities([]);
    setSelectedcodes([]);
    setSelectedDistricts(event.target.value);
    pincodeApi.getDistinctValues({ property: 'city', districts: event.target.value.toString() })
      .then((data) => {
        setCities(data?.responseData?.values);
      });
    filterCodes('districts', event.target.value);
  };
  // handle selected city
  const handleCityChange = async (event): Promise<void> => {
    setSelectedcodes([]);
    setSelectedCities(event.target.value);
    filterCodes('cities', event.target.value);
  };

  // handle selected pincode
  const handlePincodesChange = (event): void => {
    setSelectedcodes(event.target.value);
  };

  useEffect(() => {
    getStatesDetails().catch(console.error);
  }, []);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle
        sx={{ justifyContent: 'flex-end' }}
      >
        Add Service Areas
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => {
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      { isAvailable === 'Loading' && (
        <>
          <Typography
            sx={{
              ml: 4,
              mb: 4
            }}
            variant="body2"
          >
            Loading...
          </Typography>
        </>
      )}
      { isAvailable === 'Fail' && (
      <Typography
        sx={{
          ml: 4,
          mb: 4
        }}
        color="red"
        variant="body2"
      >
        Pincodes Are Not Available
      </Typography>
      )}
      { isAvailable === 'Success' && (
      <>
        <DialogContent
          sx={{
            mt: -2
          }}
        >
          <Grid
            container
            spacing={1}
          >
            <Grid
              item
              md={3}
              sm={3}
              xs={3}
            >
              <Typography
                sx={{ textAlign: 'left' }}
                variant="subtitle2"
              >
                State
              </Typography>
              <FormControl
                fullWidth
              >
                <Select
                  labelId="state-select"
                  value={selectedStates}
                  onChange={(event) => {
                    handleStateChange(event);
                  }}
                  renderValue={(t) => t}
                  style={{
                    fontSize: '12px',
                    height: '35px'
                  }}
                  MenuProps={MenuProps}
                >
                  {states?.map((item) => (
                    <MenuItem
                      key={item}
                      value={item}
                    >
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              md={3}
              sm={3}
              xs={3}
            >
              <Typography
                sx={{ textAlign: 'left' }}
                variant="subtitle2"
              >
                District
              </Typography>
              <FormControl
                fullWidth
              >
                <Select
                  labelId="state-multi-select"
                  multiple
                  disabled={!selectedStates}
                  value={selectedDistricts}
                  onChange={(event) => {
                    handleDistChange(event);
                  }}
                  renderValue={(t) => t.join(', ')}
                  style={{
                    fontSize: '12px',
                    height: '35px'
                  }}
                  MenuProps={MenuProps}
                >
                  {districts
                    .map((district) => (
                      <MenuItem
                        key={district}
                        value={district}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={
                              selectedDistricts.indexOf(district) > -1
                          }
                          />
                        </ListItemIcon>
                        <ListItemText primary={district} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              md={3}
              sm={3}
              xs={3}
            >
              <Typography
                sx={{ textAlign: 'left' }}
                variant="subtitle2"
              >
                Cities
              </Typography>
              <FormControl
                fullWidth
              >
                <Select
                  labelId="state-multi-select"
                  multiple
                  disabled={!selectedStates || selectedDistricts.length === 0}
                  value={selectedCities}
                  onChange={(event) => {
                    handleCityChange(event);
                  }}
                  renderValue={(t) => t.join(', ')}
                  style={{
                    fontSize: '12px',
                    height: '35px'
                  }}
                  MenuProps={MenuProps}
                >
                  {cities
                    .map((city) => (
                      <MenuItem
                        key={city}
                        value={city}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={
                              selectedCities.indexOf(city) > -1
                          }
                          />
                        </ListItemIcon>
                        <ListItemText primary={city} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              md={2}
              sm={2}
              xs={2}
            >
              <Typography
                sx={{ textAlign: 'left' }}
                variant="subtitle2"
              >
                Pincode
              </Typography>
              <FormControl
                fullWidth
              >
                <Select
                  labelId="pincode-multi-select"
                  multiple
                  disabled={!selectedStates}
                  value={selectedcodes}
                  onChange={(event) => handlePincodesChange(event)}
                  renderValue={
                (t) => t.join(', ')
              }
                  style={{
                    fontSize: '12px',
                    height: '35px',
                    width: '100%',
                  }}
                  MenuProps={MenuProps}
                >
                  {codes?.map((option) => (
                    <MenuItem
                      key={option}
                      value={option}
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedcodes.indexOf(option) > -1}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={option}
                      />
                    </MenuItem>
                  ))}
                </Select>

              </FormControl>
            </Grid>
            <Grid
              item
              md={1}
              sm={1}
              xs={1}
            >
              <Button
                sx={{
                  mt: 3
                }}
                disabled={selectedStates.length < 1 || selectedDistricts.length < 1}
                onClick={searchAreas}
                variant="contained"
              >
                Search
              </Button>
            </Grid>
          </Grid>
          <Box
            sx={{
              minHeight: 250
            }}
          >
            { searchClicked && (
              <>
                {pincodesArray?.length > 0 ? (
                  <>
                    <Box>
                      <Typography
                        color="textPrimary"
                        sx={{ pt: 2, mb: 1 }}
                        variant="h6"
                      >
                        Select Area
                      </Typography>
                    </Box>
                    <Grid
                      container
                    >
                      <Grid
                        item
                        md={12}
                        sm={12}
                        xs={12}
                      >
                        <TableContainer
                          sx={{ maxHeight: 200 }}
                          component={Paper}
                        >
                          <Table
                            size="small"
                            stickyHeader
                            aria-label="sticky table"
                          >
                            <TableHead>
                              <TableRow
                                key="HeaderRow"
                              >
                                <TableCell
                                  sx={{
                                    backgroundColor: 'neutral.100',
                                  }}
                                >
                                  STATE
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: 'neutral.100',
                                  }}
                                >
                                  DISTRICT
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: 'neutral.100',
                                  }}
                                >
                                  CITY
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: 'neutral.100',
                                  }}
                                >
                                  PINCODE
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: 'neutral.100',
                                  }}
                                >
                                  AREA
                                </TableCell>
                                <TableCell
                                  padding="checkbox"
                                  sx={{
                                    backgroundColor: 'neutral.100',
                                  }}
                                >
                                  {pincodesArray?.length > 0 && (
                                  <Checkbox
                                    checked={
                                    pincodesArray?.length > 0
                                    && selectedPincodes.length === pincodesArray?.length
                                  }
                                    indeterminate={
                                    selectedPincodes.length > 0
                                    && selectedPincodes.length < pincodesArray?.length
                                  }
                                    onChange={handleSelectAll}
                                    disabled={pincodesArray?.length < 1}
                                  />
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {pincodesArray?.map((pincode) => (
                                <TableRow
                                  hover
                                  key={pincode.id}
                                >
                                  <TableCell>
                                    {pincode.state}
                                  </TableCell>
                                  <TableCell>
                                    {pincode.district}
                                  </TableCell>
                                  <TableCell>
                                    {pincode.city}
                                  </TableCell>
                                  <TableCell>
                                    {pincode.code}
                                  </TableCell>
                                  <TableCell>
                                    {pincode.area}
                                  </TableCell>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={!!selectedPincodes.find(
                                        (selectedValue) => selectedValue === pincode.id
                                      )}
                                      onChange={(event) => {
                                        handleSelect(event, pincode.id);
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  selectedDistricts.length > 0 && (
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Grid
                        item
                        md={12}
                        xs={12}
                        display="contents"
                      >
                        <Typography
                          color="red"
                          variant="body2"
                          sx={{
                            fontStyle: 'italic',
                            ml: 1,
                            mt: 1
                          }}
                        >
                          No areas are available for above selection
                        </Typography>
                      </Grid>
                    </Grid>

                  )
                )}
              </>
            )}

          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            alignItems: 'right'
          }}
        >
          <Button
            color="primary"
            onClick={(e) => {
              closeDialog(e, '');
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={selectedPincodes.length < 1}
            onClick={() => { handleAddServiceAreas(); }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </>
      )}
    </Dialog>
  );
};

AddServiceArea.defaultProps = {
  open: false,
};

AddServiceArea.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  addServiceAreas: PropTypes.func,
  serviceAreaIds: PropTypes.array
};
